import {Injectable} from '@angular/core';
import {VehicleCustomerReference} from '../awp-dms-interface/models/vehicle';

/**
 * Service that coordinates customer selection when multiple customers are associated with a vehicle.
 * The overlay component registers itself with this service, and consumers (like DmsService) use
 * this service to trigger customer selection, providing loose coupling between service and UI layers.
 */
@Injectable({
  providedIn: 'root'
})
export class CustomerSelectionService {
  private selectCustomerFn: ((customers: VehicleCustomerReference[]) => Promise<VehicleCustomerReference>) | null = null;

  /**
   * Registers a function to handle customer selection (typically called by the overlay component).
   * @param selectFn Function that displays UI and returns the selected customer
   */
  registerSelectionHandler(selectFn: (customers: VehicleCustomerReference[]) => Promise<VehicleCustomerReference>): void {
    this.selectCustomerFn = selectFn;
  }

  /**
   * Unregisters the selection handler (typically called when overlay component is destroyed).
   */
  unregisterSelectionHandler(): void {
    this.selectCustomerFn = null;
  }

  /**
   * Prompts the user to select a customer from the provided list.
   * If no selection handler is registered, falls back to default selection logic.
   * @param customers List of customers to choose from
   * @returns Promise resolving to the selected customer
   */
  async selectCustomer(customers: VehicleCustomerReference[]): Promise<VehicleCustomerReference> {
    if (this.selectCustomerFn) {
      return this.selectCustomerFn(customers);
    }
    // Fallback: select first USER or first customer
    return customers.find(c => c.relationType === 'USER') ?? customers[0];
  }

  /**
   * Returns true if a selection handler is registered.
   */
  hasSelectionHandler(): boolean {
    return this.selectCustomerFn !== null;
  }
}

